/**
 * EachOtterApp Shared Styles
 *
 * ⚠️ VERALTET — nichts Neues hier hinzufügen.
 *
 * Verbindlich sind die Primitives in `components/ui/` (siehe
 * `frontend/docs/design-system/TOKENS.md`). Diese Datei existiert nur noch, weil
 * 46 Dateien sie importieren. Beim Anfassen einer Datei bitte auf `ui/Button`,
 * `ui/Card`, `ui/Input` usw. migrieren.
 *
 * Phase 5 hat sie entschärft, statt sie zu erweitern:
 * - 37 ungenutzte Konstanten entfernt (~46 % der Datei waren toter Code)
 * - Button-, Input- und Karten-Konstanten geben jetzt die Optik der `ui/`-Primitives
 *   zurück. Damit gibt es eine Wahrheit statt zwei — ohne dass eine der
 *   Aufrufstellen angefasst werden musste.
 */

import { buttonClasses, iconButtonClasses } from '../components/ui/Button';

// Gemeinsame Basis für Eingabefelder — abgestimmt auf `ui/Input`.
const INPUT_BASE =
  "w-full min-h-[48px] px-3 sm:px-4 py-3 rounded-xl border border-border bg-surface-raised " +
  "text-sm sm:text-base placeholder:text-muted-foreground transition-colors duration-base " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:border-primary";

export const SharedStyles = {

  // ==================== CONTAINERS ====================
  
  /**
   * Zentrierte Inhaltsspalte — DER Standard für Lese-/Content-Seiten
   * (Aura, Logbuch, Settings, Feedback …).
   * Mobil volle Breite, auf Desktop mittig begrenzt statt randlos gestreckt.
   */
  contentContainer: "w-full max-w-3xl mx-auto px-3 sm:px-4",

  /**
   * Haupt-Container für Inhaltsflächen.
   * Vorher Glas (backdrop-blur-xl, rounded-3xl, shadow-2xl, border-white/50) —
   * jetzt eine ruhige Flaeche. Glas bleibt der Navigation vorbehalten.
   */
  glassContainer: "relative bg-surface rounded-xl shadow-token-md border border-border overflow-hidden",

  /**
   * Sekundärer Container für verschachtelte Elemente.
   */
  glassContainerSecondary: "bg-surface-raised rounded-xl shadow-token-sm border border-border",

  // ==================== BUTTONS ====================
  // Diese Konstanten geben jetzt exakt die Klassen von `ui/Button` zurueck.
  // Vorher hatten sie eine eigene Optik (rounded-2xl, hover:scale-105, Glas-Kanten),
  // die dem Design-System widersprach. Die ~40 Aufrufstellen bleiben unveraendert —
  // die Vereinheitlichung passiert allein hier (Phase 5, "eine Wahrheit statt zwei").

  /**
   * Primärer Button (groß) — identisch zu <Button variant="primary" size="md">
   */
  buttonPrimary: buttonClasses('primary', 'md'),

  /**
   * Primärer Button (kompakt) — identisch zu <Button variant="primary" size="sm">
   */
  buttonPrimarySmall: buttonClasses('primary', 'sm'),

  /**
   * Sekundärer Button — identisch zu <Button variant="secondary" size="md">
   */
  buttonSecondary: buttonClasses('secondary', 'md'),

  /**
   * Kleiner Button — identisch zu <Button variant="secondary" size="sm">
   */
  buttonSmall: buttonClasses('secondary', 'sm'),

  /**
   * Magic Button Subtle (MOBILE OPTIMIZED)
   * Dezenter Purple-Blue Gradient mit subtilen Hover-Effekten
   * Inspiriert vom "KI-Lernziele generieren" Button - viel dezenter
   */
  buttonMagicSubtle: "min-h-[48px] px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-medium transition-all duration-300 bg-primary-soft/60 hover:bg-primary-soft border border-primary/30 hover:border-primary/40 hover:shadow-token-md text-foreground",

  /**
   * Magic Button Subtle – kompakt (MOBILE OPTIMIZED)
   * Gleiche dezente Aura-Anmutung wie buttonMagicSubtle, aber in den kompakten
   * Maßen von buttonPrimarySmall – für Toolbars/Listen-Header (z.B. "Neuer Eintrag").
   */
  buttonMagicSubtleSmall: "min-h-[44px] px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 bg-primary-soft/60 hover:bg-primary-soft border border-primary/30 hover:border-primary/40 hover:shadow-token-sm text-foreground",

  /**
   * Icon Button — quadratisch, ohne Glas und ohne Scale-Sprung.
   * Identisch zu `iconButtonClasses()` aus `ui/Button`.
   */
  buttonIcon: iconButtonClasses(),

  // ==================== FORM ELEMENTS ====================
  // Wie bei den Buttons: eine Definition, abgestimmt auf `ui/Input`
  // (rounded-xl, focus-visible:ring-2, Token-Flaechen statt Glas).
  // `inputField` und `inputFieldSolid` sind jetzt identisch — die frühere
  // Unterscheidung (Glas vs. solide) war genau die Inkonsistenz.

  /**
   * Eingabefeld. iOS-Zoom wird global via index.html verhindert (font-size: 16px).
   */
  inputField: INPUT_BASE,

  /**
   * Mehrzeiliges Eingabefeld.
   */
  textareaField: `${INPUT_BASE} min-h-[120px] resize-none`,

  // ==================== NAVIGATION ====================
  
  /**
   * Tab Navigation Container
   * Glassmorphism Container für Tabs
   */
  tabContainer: "bg-surface/60 backdrop-blur-xl rounded-xl p-2 shadow-token-lg border border-border",
  
  /**
   * Aktiver Tab (MOBILE OPTIMIZED) — Liquid-Glass-Kapsel wie in der Navbar.
   * Zarte Glasfläche + feine Lichtkante + Marken-Text (subtil statt vollflächig gefüllt).
   */
  tabActive: "min-h-[48px] px-3 sm:px-4 py-2 bg-surface/80 backdrop-blur-sm text-primary ring-1 ring-primary/15 shadow-token-sm rounded-xl font-medium transition-all duration-300",

  /**
   * Inaktiver Tab (MOBILE OPTIMIZED) — transparent (nur aktiver Tab trägt die Glas-Kapsel),
   * gedämpfter Text mit dezentem Hover. So wirkt die Leiste als ein zusammenhängendes Segmented Control.
   */
  tabInactive: "min-h-[48px] px-3 sm:px-4 py-2 bg-transparent text-muted-foreground hover:text-foreground hover:bg-surface/50 rounded-xl font-medium transition-all duration-300",

  // ==================== GLAS-PILLS V2 (blur-frei, abgestimmt auf ProfileCardV2) ====================
  /**
   * Box-Shadow-Rezepte als Inline-Style-Strings (Hairline-Border + sanfter Schatten).
   * Bewusst KEIN backdrop-blur -> performant in großen Grids.
   * Verwendung: style={{ boxShadow: SharedStyles.glassShadows.hairline }}
   *
   * Die Werte selbst liegen als CSS-Custom-Properties in index.css (`--glass-*`)
   * und werden im Dark-Theme überschrieben. Vorher standen hier feste
   * Hell-Modus-rgba-Werte -> im Dunkeln eine fast weiße Innenlinie.
   */
  glassShadows: {
    hairline: 'var(--glass-hairline)',
    violetHairline: 'var(--glass-hairline-violet)',
    emeraldHairline: 'var(--glass-hairline-emerald)',
    amberHairline: 'var(--glass-hairline-amber)',
    cardLift: 'var(--glass-lift)',
    violetLift: 'var(--glass-lift-violet)',
  },

  // ==================== CARDS ====================

  /**
   * Standard-Karte — gleiche Optik wie `ui/Card` (variant "raised"), plus Innenabstand.
   * Vorher: bg-surface/80 + backdrop-blur-md + rounded-2xl + shadow-lg + border-white/30.
   * Die weisse Kante war im Dark-Theme derselbe Fehlertyp wie die frueher
   * reparierten glassShadows; der Blur kostete in grossen Listen Leistung.
   */
  card: "bg-surface border border-border rounded-xl shadow-token-sm p-3 sm:p-4",

  /**
   * Info Card
   * Für Informations-Anzeigen
   */
  cardInfo: "bg-accent/10 backdrop-blur-sm rounded-xl border border-accent/30 p-3 sm:p-4 text-accent",
  
  /**
   * Warning Card
   * Für Warnungen
   */
  cardWarning: "bg-warning-soft backdrop-blur-sm rounded-xl border border-warning/40 p-3 sm:p-4 text-foreground",
  
  /**
   * Error Card
   * Für Fehlermeldungen
   */
  cardError: "bg-danger-soft backdrop-blur-sm rounded-xl border border-danger/40 p-3 sm:p-4 text-foreground",

  // ==================== TYPOGRAPHY ====================
  
  /**
   * Haupttitel
   * Große, prominente Überschriften
   */
  
  /**
   * Sektions-Titel
   * Mittlere Überschriften
   */
  titleSection: "text-base sm:text-lg font-semibold text-foreground dark:text-foreground",

  /**
   * Untertitel
   * Kleinere Überschriften
   */
  titleSub: "text-sm sm:text-base font-medium text-foreground/80",

  /**
   * Sekundärer Text
   * Weniger prominenter Text
   */
  textSecondary: "text-xs sm:text-sm text-muted-foreground",

  /**
   * Caption Text
   * Sehr kleiner Text für Bildunterschriften etc.
   */
  textCaption: "text-xs text-muted-foreground",

  // ==================== LAYOUT PATTERNS ====================

  /**
   * Mobile Action Buttons
   * Gestapelt auf Mobile, horizontal auf Desktop
   */
  actionButtons: "flex flex-col sm:flex-row gap-3 sm:gap-4",


  // ==================== SOLID INPUT FIELDS ====================

  /**
   * Eingabefeld — identisch zu `inputField`. Die frühere Unterscheidung
   * "Glas vs. solide" war genau die Inkonsistenz, die es zu beseitigen galt.
   */
  inputFieldSolid: INPUT_BASE,

  /**
   * Mehrzeiliges Eingabefeld — identisch zu `textareaField`.
   */
  textareaFieldSolid: `${INPUT_BASE} min-h-[120px] resize-none`,

  // ==================== ADDITIONAL BUTTON VARIANTS ====================
  // Ebenfalls auf `ui/Button` umgeleitet — vorher: Glas, rounded-2xl,
  // hover:scale-105 und rohe Palettenfarben (bg-red-500/80) statt Tokens.

  /**
   * Danger Button — identisch zu <Button variant="danger" size="md">
   */
  buttonDanger: buttonClasses('danger', 'md'),

  /**
   * Success Button — identisch zu <Button variant="success" size="md">
   */
  buttonSuccess: buttonClasses('success', 'md'),

  /**
   * Ghost Button — identisch zu <Button variant="ghost" size="md">
   */
  buttonGhost: buttonClasses('ghost', 'md'),

  /**
   * Ehemals Verlaufs-CTA. Verläufe in Buttons verstossen gegen die
   * De-Slop-Regel des Design-Systems -> jetzt die Primaerflaeche.
   */
  buttonGradient: buttonClasses('primary', 'md'),

  /**
   * Link Button — Text ohne Flaeche.
   */
  buttonLink: "min-h-[44px] px-2 py-1 text-primary hover:text-primary-hover font-medium transition-colors underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-lg",

  // ==================== STATUS BADGES ====================

  /**
   * Success Badge (MOBILE FIRST)
   * Grün für erfolgreiche Aktionen
   */
  badgeSuccess: "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-success-soft text-foreground border border-success/40",

  /**
   * Warning Badge (MOBILE FIRST)
   * Gelb/Amber für Warnungen
   */
  badgeWarning: "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-warning-soft text-foreground border border-warning/40",

  /**
   * Error Badge (MOBILE FIRST)
   * Rot für Fehler
   */
  badgeError: "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-danger-soft text-foreground border border-danger/40",

  /**
   * Info Badge (MOBILE FIRST)
   * Blau für Informationen
   */
  badgeInfo: "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent/15 text-accent border border-accent/30",

  /**
   * Neutral Badge (MOBILE FIRST)
   * Grau für Default Badge
   */
  badgeNeutral: "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-surface-raised text-foreground border border-border",

  // ==================== ADDITIONAL CARD VARIANTS ====================

  /**
   * Solid Card — fuer Dashboard-Panels, eine Elevation-Stufe ueber `card`.
   */
  cardSolid: "bg-surface rounded-xl shadow-token-md border border-border p-3 sm:p-4",
};

/**
 * Hilfsfunktionen für dynamische Klassen
 */
export const StyleHelpers = {
  
  /**
   * Kombiniert Standard-Klassen mit Custom-Klassen
   * @param {string} baseStyle - Basis-Style aus SharedStyles
   * @param {string} customClasses - Zusätzliche Klassen
   * @returns {string} Kombinierte Klassen
   */
  combine: (baseStyle, customClasses = "") => {
    return `${baseStyle} ${customClasses}`.trim();
  },
  
  /**
   * Erstellt bedingte Klassen
   * @param {boolean} condition - Bedingung
   * @param {string} trueClasses - Klassen wenn true
   * @param {string} falseClasses - Klassen wenn false
   * @returns {string} Bedingte Klassen
   */
  conditional: (condition, trueClasses, falseClasses = "") => {
    return condition ? trueClasses : falseClasses;
  },
  
  /**
   * Erstellt Button mit korrektem Typ
   * @param {string} type - 'primary' | 'primarySmall' | 'secondary' | 'danger' | 'success' | 'ghost' | 'gradient' | 'link' | 'small' | 'icon'
   * @returns {string} Entsprechende Button Klassen
   */
  button: (type = 'primary') => {
    const types = {
      primary: SharedStyles.buttonPrimary,
      primarySmall: SharedStyles.buttonPrimarySmall,
      secondary: SharedStyles.buttonSecondary,
      danger: SharedStyles.buttonDanger,
      success: SharedStyles.buttonSuccess,
      ghost: SharedStyles.buttonGhost,
      gradient: SharedStyles.buttonGradient,
      link: SharedStyles.buttonLink,
      small: SharedStyles.buttonSmall,
      icon: SharedStyles.buttonIcon,
      magicSubtle: SharedStyles.buttonMagicSubtle,
      magicSubtleSmall: SharedStyles.buttonMagicSubtleSmall
    };
    return types[type] || types.primary;
  },

  /**
   * Erstellt Badge mit korrektem Typ
   * @param {string} type - 'success' | 'warning' | 'error' | 'info' | 'neutral'
   * @returns {string} Entsprechende Badge Klassen
   */
  badge: (type = 'neutral') => {
    const types = {
      success: SharedStyles.badgeSuccess,
      warning: SharedStyles.badgeWarning,
      error: SharedStyles.badgeError,
      info: SharedStyles.badgeInfo,
      neutral: SharedStyles.badgeNeutral
    };
    return types[type] || types.neutral;
  }
};

/**
 * Beispiel-Verwendung (Bestandscode — fuer NEUEN Code bitte `components/ui/` nutzen):
 *
 * import { SharedStyles, StyleHelpers } from '../styles/SharedStyles';
 *
 * <div className={SharedStyles.card}>                       // Karte
 * <div className={SharedStyles.cardSolid}>                  // Panel-Karte
 * <input className={SharedStyles.inputFieldSolid} />        // Eingabefeld
 * <button className={SharedStyles.buttonPrimary}>Speichern</button>
 * <span className={SharedStyles.badgeSuccess}>Aktiv</span>
 *
 * // Mit Custom-Klassen kombinieren
 * <div className={StyleHelpers.combine(SharedStyles.card, "mb-4")}>
 *
 * // Bedingte Klassen
 * <button className={StyleHelpers.conditional(isActive, SharedStyles.tabActive, SharedStyles.tabInactive)}>
 *
 * // Dynamisch
 * <button className={StyleHelpers.button('danger')}>Loeschen</button>
 * <span className={StyleHelpers.badge('success')}>Aktiv</span>
 */

export default SharedStyles;